import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icono } from './Icono';

interface PropsModal {
  abierto: boolean;
  alCerrar: () => void;
  titulo: string;
  children: ReactNode;
  /** Botones de abajo. Quedan siempre a la vista aunque el cuerpo se desplace. */
  pie?: ReactNode;
  ancho?: number;
}

const ENFOCABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ abierto, alCerrar, titulo, children, pie, ancho = 480 }: PropsModal) {
  const ventana = useRef<HTMLDivElement>(null);
  const idTitulo = useId();

  // Se guarda en una referencia para que el efecto no se reinicie (y robe el foco)
  // cada vez que la pantalla pasa una función nueva.
  const cerrar = useRef(alCerrar);
  useEffect(() => { cerrar.current = alCerrar; }, [alCerrar]);

  useEffect(() => {
    if (!abierto) return undefined;

    const anterior = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const desbordeAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const primero = ventana.current?.querySelector<HTMLElement>(ENFOCABLES);
    (primero ?? ventana.current)?.focus();

    function alTeclear(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        cerrar.current();
        return;
      }
      if (e.key !== 'Tab' || !ventana.current) return;
      const enfocables = ventana.current.querySelectorAll<HTMLElement>(ENFOCABLES);
      const inicio = enfocables[0];
      const fin = enfocables[enfocables.length - 1];
      if (!inicio || !fin) return;
      if (e.shiftKey && document.activeElement === inicio) {
        e.preventDefault();
        fin.focus();
      } else if (!e.shiftKey && document.activeElement === fin) {
        e.preventDefault();
        inicio.focus();
      }
    }

    window.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('keydown', alTeclear);
      document.body.style.overflow = desbordeAnterior;
      anterior?.focus();
    };
  }, [abierto]);

  if (!abierto) return null;

  return createPortal(
    <div className="modal" onClick={alCerrar} role="presentation">
      <div
        ref={ventana}
        className="modal__ventana"
        style={{ width: ancho }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className="modal__cabecera">
          <h2 id={idTitulo} className="modal__titulo">{titulo}</h2>
          <button type="button" className="modal__cerrar" onClick={alCerrar} aria-label="Cerrar">
            <Icono nombre="x" tamano={18} />
          </button>
        </div>
        <div className="modal__cuerpo">{children}</div>
        {pie && <div className="modal__pie">{pie}</div>}
      </div>
    </div>,
    document.body,
  );
}
